import { ApolloProvider } from "@apollo/client";
import { LightingConverger } from "components/global/components/LightingConverger";
import { LightingDebugOverlay } from "components/global/components/LightingDebugOverlay";
import { NetworkDebugOverlay } from "components/global/components/NetworkDebugOverlay";
import { AlarmAudioProvider } from "contexts/AlarmAudioContext";
import { DeviceContextProvider } from "contexts/DeviceContext";
import { changeLanguage } from "i18next";
import { useCallback, useEffect } from "react";
import { type EventsType, IdleTimerProvider, type PresenceType } from "react-idle-timer";
import { ConnectionErrorModal } from "./components/global/modals/ConnectionErrorModal";
import { useIdleWakeAction } from "./hooks/useIdleWakeAction";
import { useAppDispatch } from "./hooks/utilsHooks/storeHooks";
import { MealsListPage } from "./pages";
import { Config } from "./services/configuration/eatSmartConfig";
import "./services/i18n/config";
import { setAppState, setLocale } from "./slices/appStateSlice";
import type { AppState } from "./types/mainTypes";
import { useFridgeApolloClient } from "./utils/ApolloClientProvider";
import { getLocale } from "./utils/browser/localStorage";
import { shouldTraceLightingDebug } from "./utils/debug/debugTrace";
import { shouldTraceNetworkDebug } from "./utils/debug/networkTrace";

// react-idle-timer's default event list includes `visibilitychange` and `focus`, which fire
// constantly on iPadOS/WKWebView kiosk tablets (screen dim, Auto-Lock, Low Power Mode, app-switcher,
// Guided Access) — they were registering as "activity" and flapping `appState` ACTIVE↔IDLE, which
// blinked the fridge lights. Restrict activity detection to genuine user interaction so only real
// taps / scrolls / key input reset presence. Module-level constant = stable reference (no re-bind).
const IDLE_ACTIVITY_EVENTS: EventsType[] = ["touchstart", "touchmove", "mousedown", "keydown", "wheel"];

export const App = () => {
	const { client, isConnected, setIsConnected, isSubscriptionBroken, setIsSubscriptionBroken } =
		useFridgeApolloClient();
	const dispatch = useAppDispatch();

	// App must NOT subscribe to appState: that subscription re-rendered App on every idle/active edge
	// and (pre-memoization) rebuilt the Apollo client, restarting INITIAL_STATE_QUERY (the
	// handleSetInitialData refire bug). The wake callback reads the store at invocation time instead.
	const wakeOnAction = useIdleWakeAction();

	const locale = getLocale();

	useEffect(() => {
		setLocale(locale);
		changeLanguage(locale);
	}, [locale]);

	// Stable so react-idle-timer doesn't re-register its handler if App ever re-renders.
	const onPresenceChange = useCallback(
		(presence: PresenceType) => {
			dispatch(setAppState(presence.type as AppState));
		},
		[dispatch],
	);

	return (
		<ApolloProvider client={client}>
			<IdleTimerProvider
				timeout={Config.activityMonitor.inactiveTimeout}
				events={IDLE_ACTIVITY_EVENTS}
				onAction={wakeOnAction}
				onPresenceChange={onPresenceChange}
			>
				<DeviceContextProvider
					client={client}
					appConnected={isConnected}
					setIsConnected={setIsConnected}
					isSubscriptionBroken={isSubscriptionBroken}
					setIsSubscriptionBroken={setIsSubscriptionBroken}
				>
					{/* Mounted once for the app's lifetime so the open-door alarm MP3s are fetched a single
					    time per app session and reused across every order (the kiosk has the HTTP cache
					    disabled — see AlarmAudioProvider). */}
					<AlarmAudioProvider>
						<LightingConverger />
						<MealsListPage />
						{shouldTraceLightingDebug() && <LightingDebugOverlay />}
						{shouldTraceNetworkDebug() && <NetworkDebugOverlay />}
						{!isConnected && (
							<ConnectionErrorModal appConnected={isConnected} isSubscriptionBroken={isSubscriptionBroken} />
						)}
					</AlarmAudioProvider>
				</DeviceContextProvider>
			</IdleTimerProvider>
		</ApolloProvider>
	);
};
